import { useState } from 'react'
import {
    useNavigate,
    useNavigationType,
    useParams,
    useSearchParams,
} from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import posthog from 'posthog-js'
import { FiCheckCircle, FiChevronLeft, FiLock } from 'react-icons/fi'
import Header from 'components/headers/addLoan'
import { PulseCard, SectionError } from 'components/states'
import {
    agreeToLoanDemand,
    getLoanDemand,
    objectToLoanDemand,
    postLoanDemandComment,
    refusalMessage,
} from 'services/loanDemands'
import { faPlain, longJalali, toman } from 'utilities/helper/format'
import { demandPriority } from 'utilities/helper/demandPriority'
import { inTimelineOrder } from 'utilities/helper/timeline'
import { grantRouteFor } from '../grantFromDemand'
import ManagersOnly from '../components/ManagersOnly'
import MemberGlance from './MemberGlance'
import TransferTrackingLink from './TransferTrackingLink'

/**
 * The timeline reads in the second person for the member whose request it is
 * and in the third for a manager reading somebody else's. Same rows, same
 * order — only the voice changes. Getting that wrong is how a manager ends up
 * reading «درخواستت پذیرفته شد» about a loan he granted to someone else.
 */
const EVENT_LABELS: Record<ILoanDemandEventKind, string> = {
    crt: 'درخواست ثبت شد',
    ntf: 'به مدیران صندوق اطلاع داده شد',
    cmt: 'پیام',
    edt: 'درخواست ویرایش شد',
    acc: 'درخواست پذیرفته شد',
    pay: 'وام پرداخت شد',
    rej: 'درخواست رد شد',
    cnl: 'درخواست را لغو کردید',
    lik: 'موافقت کرد',
    dis: 'مخالفت کرد',
    mnt: 'یادداشت برای مدیران',
}

/** The vote, whose rows read «<name> موافقت کرد». */
const VOTE_KINDS: ILoanDemandEventKind[] = ['lik', 'dis']

/**
 * The kinds only managers are ever sent: the vote and «یادداشت برای مدیران».
 * The backend already strips them for anyone else; this is only what decides
 * to draw the lock next to them.
 */
const MANAGER_ONLY_KINDS: ILoanDemandEventKind[] = [...VOTE_KINDS, 'mnt']

const MANAGER_EVENT_LABELS: Record<ILoanDemandEventKind, string> = {
    ...EVENT_LABELS,
    cnl: 'عضو درخواست را لغو کرد',
}

// Keyed on the backend's REAL state codes (`choice.LOAN_DEMAND_STATE_*`):
// `inas` and `acpd`. The first version of this page guessed `insp`/`accp`,
// so every accepted request rendered an empty, colourless status chip.
const STATE_LABELS: Record<ILoanDemandState, string> = {
    dmnd: 'در انتظار بررسی',
    inas: 'در حال بررسی',
    acpd: 'پذیرفته شد',
    rjcd: 'رد شد',
    cnld: 'لغو شد',
}

const STATE_TONE: Record<ILoanDemandState, string> = {
    dmnd: 'bg-warning-soft text-warning-on',
    inas: 'bg-warning-soft text-warning-on',
    acpd: 'bg-success-2 text-success-on',
    rjcd: 'bg-danger-soft text-danger-on',
    cnld: 'bg-canvas text-text-secondary',
}

/**
 * Whether this visit came from the SMS or from inside the app. Same reasoning
 * as the withdrawal-request detail page: a tap in the app is a router PUSH
 * against a document we rendered, while the SMS link is a fresh load through
 * the backend's `/l/<id>/` redirect and arrives as POP with a foreign or
 * absent referrer.
 */
const arrivalSource = (navigationType: string) => {
    if (navigationType !== 'POP') return 'app'
    if (!document.referrer) return 'sms'
    try {
        return new URL(document.referrer).origin === window.location.origin
            ? 'app'
            : 'sms'
    } catch {
        return 'sms'
    }
}

/**
 * One درخواست وام and everything that has happened to it — the کارتابل.
 *
 * This page exists because of the silence. A member files the single most
 * consequential ask they ever make of their fund, sees «در انتظار بررسی», and
 * then hears nothing until a decision lands. From the outside a fund still
 * discussing the request and a fund that never opened it are identical. The
 * managers' discussion, meanwhile, happened in a Telegram group, so the reason
 * for the answer never reached the person it was about.
 *
 * It is the target of the پیگیری link in every SMS this feature sends, reached
 * through the backend's short `/l/<id>/` redirect — and those SMSes go to
 * managers as well as to the requester, so both sides must land on something
 * that makes sense. `viewer` in the payload is what decides which.
 */
export default function LoanDemandDetail() {
    const params = useParams()
    const navigate = useNavigate()
    const navigationType = useNavigationType()
    const queryClient = useQueryClient()
    const [searchParams] = useSearchParams()
    const [draft, setDraft] = useState('')
    /**
     * Who a manager's message is for. Starts on the managers: a note sent to
     * the member by mistake is read and texted and cannot be taken back, while
     * one kept among the managers by mistake costs a second tap.
     */
    const [audience, setAudience] = useState<TLoanDemandAudience>('managers')

    const demandId = params?.id
    const fundId = searchParams.get('fund')

    const { data, isLoading, isError, error, refetch } = useQuery(
        ['loan-demand', fundId, demandId],
        getLoanDemand,
        { enabled: Boolean(fundId && demandId) }
    )

    // 403 means «not your request and you don't manage this fund» — permanent.
    // Offering «تلاش دوباره» for it sends people round a loop that can never
    // end differently.
    const status = (error as { response?: { status?: number } })?.response
        ?.status
    const notAllowed = status === 403 || status === 404
    // The API route carries the fund in the path, so without `?fund=` the query
    // stays disabled — which also means `isLoading` is FALSE. Rendering off
    // `isLoading || data` alone would show a blank page, which is exactly what
    // a link missing the parameter would do.
    const missingFund = !fundId

    const isManager = Boolean(data?.viewer?.is_manager)
    const isRequester = Boolean(data?.viewer?.is_requester)
    const eventLabels = isManager ? MANAGER_EVENT_LABELS : EVENT_LABELS
    // `?? true` on the two flags only matters for a backend older than this
    // bundle; the server refuses anything the rules do not allow regardless
    const canAgree = isManager && (data?.viewer?.can_agree ?? true)
    const canObject = isManager && (data?.viewer?.can_object ?? false)
    const agreeCount = (data?.managers ?? []).filter(
        (manager) => manager.stance === 'agree' || manager.liked
    ).length
    const disagreeCount = (data?.managers ?? []).filter(
        (manager) => manager.stance === 'disagree'
    ).length
    // Strictly `=== true`: only a backend that sends the flag understands
    // `audience`, and an older one would turn a private note into a public
    // comment and an SMS to the member.
    const canNote = isManager && data?.viewer?.can_note === true
    const noting = canNote && audience === 'managers'

    const chooseAudience = (next: TLoanDemandAudience) => {
        if (next === audience) return
        setAudience(next)
        posthog.capture('loan_demand_comment_audience_changed', {
            fund_id: fundId,
            demand_id: demandId,
            audience: next,
        })
    }

    const comment = useMutation(postLoanDemandComment, {
        onSuccess: (_event, variables) => {
            const isNote = variables.audience === 'managers'
            posthog.capture('loan_demand_comment_posted', {
                fund_id: fundId,
                demand_id: demandId,
                author: isRequester ? 'requester' : 'manager',
                kind: isNote ? 'note' : 'comment',
                audience_offered: canNote,
                // never the text itself
                length: variables.note.length,
            })
            if (variables.audience) {
                toast.success(
                    isNote
                        ? 'یادداشت فقط برای مدیران ثبت شد'
                        : 'پیام برای عضو فرستاده شد'
                )
            }
            setDraft('')
            queryClient.invalidateQueries(['loan-demand', fundId, demandId])
        },
        onError: () => {
            toast.error('پیام ثبت نشد. دوباره تلاش کنید.')
        },
    })

    /**
     * The vote. Both halves open a confirmation first rather than firing on
     * the tap: «موافقم» cannot be taken back, and «مخالفم» needs its reason.
     */
    const [voting, setVoting] = useState<'agree' | 'disagree' | null>(null)
    const [reason, setReason] = useState('')
    const viewerStance = data?.viewer?.stance ?? null

    const openVote = (stance: 'agree' | 'disagree') => {
        setVoting(stance)
        posthog.capture('loan_demand_vote_started', {
            fund_id: fundId,
            demand_id: demandId,
            stance,
            previous_stance: viewerStance,
        })
    }

    const onVoteSaved = (stance: 'agree' | 'disagree') => {
        posthog.capture('loan_demand_vote_recorded', {
            fund_id: fundId,
            demand_id: demandId,
            stance,
            previous_stance: viewerStance,
            // never the text itself
            reason_length: stance === 'disagree' ? reason.trim().length : 0,
        })
        setVoting(null)
        setReason('')
        toast.success(
            stance === 'agree' ? 'موافقت شما ثبت شد' : 'مخالفت شما ثبت شد'
        )
        queryClient.invalidateQueries(['loan-demand', fundId, demandId])
    }

    const onVoteFailed = (stance: 'agree' | 'disagree', error: unknown) => {
        const message = refusalMessage(error)
        posthog.capture('loan_demand_vote_failed', {
            fund_id: fundId,
            demand_id: demandId,
            stance,
            refused: Boolean(message),
        })
        toast.error(message || 'ثبت نشد. دوباره تلاش کنید.')
        // a refusal means the page is behind the server — e.g. the manager
        // already voted from another device — so show them what is true now
        if (message) {
            setVoting(null)
            queryClient.invalidateQueries(['loan-demand', fundId, demandId])
        }
    }

    const agree = useMutation(agreeToLoanDemand, {
        onSuccess: () => onVoteSaved('agree'),
        onError: (error) => onVoteFailed('agree', error),
    })

    const objection = useMutation(objectToLoanDemand, {
        onSuccess: () => onVoteSaved('disagree'),
        onError: (error) => onVoteFailed('disagree', error),
    })

    const grantThis = () => {
        if (!data || !fundId) return
        posthog.capture('loan_grant_started_from_demand', {
            fund_id: fundId,
            demand_id: demandId,
            // the wizard now opens on step 2 with this member chosen
            prefilled: true,
            agree_count: data.managers.filter(
                (manager) => manager.stance === 'agree'
            ).length,
            disagree_count: data.managers.filter(
                (manager) => manager.stance === 'disagree'
            ).length,
        })
        const route = grantRouteFor(
            fundId,
            {
                demandId: data.id,
                poolshipId: data.poolship_id,
                memberId: data.member_id,
                memberName: data.member_name,
                amount: data.amount,
                numRepayments: data.num_repayments,
                priority: data.demand_priority,
                description: data.user_description,
                requestedJalali: data.created,
                cardId: data.bank_account_id,
            },
            'cartable',
            data.moneypool_name
        )
        navigate(route.to, { state: route.state })
    }

    /**
     * The loan this request became. Same route the fund's وام‌ها list uses;
     * the borrower's poolship and the loan id both ride on this payload, so
     * no extra lookup is needed to build it.
     */
    const openLoan = () => {
        if (!data?.loan_id || !fundId) return
        posthog.capture('loan_opened_from_demand', {
            fund_id: fundId,
            demand_id: demandId,
            loan_id: data.loan_id,
            viewer: isManager ? 'manager' : 'requester',
        })
        navigate(
            `/fund/financial-history/funds-detail/${fundId}/${data.poolship_id}/${data.loan_id}`,
            { state: { name: data.member_name } }
        )
    }

    const priority = data ? demandPriority(data.demand_priority) : null

    return (
        <div className=" min-h-screen bg-canvas pb-40">
            <Header title="درخواست وام" />

            <div className=" px-4 pt-4">
                {missingFund && (
                    <SectionError message="این لینک ناقص است — شمارهٔ صندوق را ندارد. از داخل برنامه وارد شوید." />
                )}

                {!missingFund && isLoading && <PulseCard />}

                {!missingFund && isError && notAllowed && (
                    <SectionError message="این درخواست وام مال شما نیست و مدیر این صندوق هم نیستید." />
                )}

                {!missingFund && isError && !notAllowed && (
                    <SectionError
                        message="درخواست وام باز نشد."
                        onRetry={refetch}
                    />
                )}

                {data && (
                    <>
                        {/* ── the request itself ───────────────────────── */}
                        <div className=" rounded-2xl border border-stroke bg-white p-4">
                            <div className=" flex items-start justify-between gap-2">
                                <div className=" min-w-0">
                                    <p className=" text-lg font-bold text-heading">
                                        {toman(data.amount)}
                                    </p>
                                    <p className=" mt-1 text-[11px] text-text-secondary">
                                        {faPlain.format(data.num_repayments)}{' '}
                                        قسط
                                        {/* a Persian comma, never «·» — a
                                            middle dot against a digit reads
                                            as an extra zero on a phone */}
                                        {'، '}
                                        {isManager
                                            ? data.member_name
                                            : data.moneypool_name}
                                    </p>
                                </div>
                                <span
                                    className={` shrink-0 rounded-full px-3 py-1 text-[11px] font-bold ${
                                        STATE_TONE[data.demand_state]
                                    }`}
                                >
                                    {STATE_LABELS[data.demand_state]}
                                </span>
                            </div>

                            {priority && (
                                <p className=" mt-2 text-[11px] text-text-secondary">
                                    {priority.label} — {priority.need}
                                </p>
                            )}

                            {/* A member who never opened the app should not
                                find a request under their name with no
                                explanation of where it came from. */}
                            {data.registered_by_manager && (
                                <p className=" mt-3 rounded-lg bg-canvas px-3 py-2 text-[11px] leading-6 text-text-body">
                                    {isRequester
                                        ? `این درخواست را ${data.registrar_name} (مدیر صندوق) از طرف شما ثبت کرده است.`
                                        : `این درخواست را ${data.registrar_name} از طرف عضو ثبت کرده است.`}
                                </p>
                            )}

                            {data.user_description && (
                                <p className=" mt-3 border-t border-divider pt-3 text-[11px] leading-6 text-text-body">
                                    {isRequester ? 'توضیح شما' : 'توضیح عضو'}:{' '}
                                    {data.user_description}
                                </p>
                            )}
                        </div>

                        {/* ── the member, in one look ──────────────────── */}
                        {isManager && fundId && (
                            <MemberGlance
                                fundId={fundId}
                                demandId={demandId}
                                poolshipId={data.poolship_id}
                                memberName={data.member_name}
                                requestAmount={data.amount}
                            />
                        )}

                        {/* ── the managers' vote ───────────────────────── */}
                        {/* Managers only, and the server enforces it: for the
                            requester `managers` arrives empty. The lock says
                            so, because a manager who fears the member will
                            read their «مخالفم» does not write it. */}
                        {isManager && (
                            <div className=" mt-4 rounded-2xl border border-stroke bg-white p-4">
                                <div className=" flex items-center justify-between gap-2">
                                    <p className=" flex items-center gap-1.5 text-sm font-bold text-heading">
                                        <FiLock
                                            size={14}
                                            aria-hidden
                                            className=" text-text-secondary"
                                        />
                                        نظر مدیران
                                    </p>
                                    <p className=" text-[11px] text-text-secondary">
                                        {faPlain.format(agreeCount)} موافق
                                        {disagreeCount > 0
                                            ? `، ${faPlain.format(
                                                  disagreeCount
                                              )} مخالف`
                                            : ''}{' '}
                                        از{' '}
                                        {faPlain.format(data.managers.length)}
                                    </p>
                                </div>
                                <p className=" mt-1 text-[11px] leading-6 text-text-secondary">
                                    متقاضی این بخش را نمی‌بیند؛ موافقت یا مخالفت
                                    شما فقط برای مدیران صندوق نمایش داده می‌شود.
                                </p>

                                {/* Every manager, including the ones who have
                                    not answered — «کی لایک کرده و کی نه». */}
                                <ul className=" mt-3 flex flex-wrap gap-2">
                                    {data.managers.map((manager) => (
                                        <li
                                            key={manager.poolship_id}
                                            className={` rounded-full px-3 py-1 text-[11px] ${
                                                manager.stance === 'agree'
                                                    ? ' bg-success-2 font-bold text-success-on'
                                                    : manager.stance ===
                                                      'disagree'
                                                    ? ' bg-danger-soft font-bold text-danger-on'
                                                    : ' bg-canvas text-text-secondary'
                                            }`}
                                        >
                                            {manager.stance === 'agree'
                                                ? '✓ '
                                                : manager.stance === 'disagree'
                                                ? '✕ '
                                                : ''}
                                            {manager.name}
                                            {!manager.stance && (
                                                <span className=" text-text-muted">
                                                    {' '}
                                                    — هنوز نظر نداده
                                                </span>
                                            )}
                                        </li>
                                    ))}
                                </ul>

                                {data.is_open && viewerStance === 'agree' && (
                                    <p className=" mt-4 rounded-xl bg-success-2 px-3 py-2.5 text-center text-xs font-bold text-success-on">
                                        ✓ شما با این درخواست موافقت کرده‌اید
                                    </p>
                                )}

                                {data.is_open &&
                                    viewerStance === 'disagree' &&
                                    !voting && (
                                        <p className=" mt-4 rounded-xl bg-danger-soft px-3 py-2.5 text-[11px] leading-6 text-danger-on">
                                            مخالفت شما ثبت شده است. اگر نظرتان
                                            عوض شد، هنوز می‌توانید موافقت کنید.
                                        </p>
                                    )}

                                {/* the two buttons, when there is still a
                                    choice to make and nothing half-open */}
                                {data.is_open &&
                                    !voting &&
                                    (canAgree || canObject) && (
                                        <div className=" mt-4 flex gap-2">
                                            {canAgree && (
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        openVote('agree')
                                                    }
                                                    className=" flex-1 rounded-full bg-green-1 py-3 text-sm font-bold text-white"
                                                >
                                                    موافقم
                                                </button>
                                            )}
                                            {canObject && (
                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        openVote('disagree')
                                                    }
                                                    className=" flex-1 rounded-full border border-danger-on bg-white py-3 text-sm font-bold text-danger-on"
                                                >
                                                    مخالفم
                                                </button>
                                            )}
                                        </div>
                                    )}

                                {/* «موافقم» is final, so it is confirmed once */}
                                {voting === 'agree' && (
                                    <div className=" mt-4 rounded-xl border border-stroke bg-canvas p-3">
                                        <p className=" text-xs font-bold text-heading">
                                            موافقت شما ثبت شود؟
                                        </p>
                                        <p className=" mt-1 text-[11px] leading-6 text-text-secondary">
                                            موافقت بعد از ثبت قابل پس گرفتن
                                            نیست.
                                        </p>
                                        <div className=" mt-3 flex gap-2">
                                            <button
                                                type="button"
                                                disabled={agree.isLoading}
                                                onClick={() =>
                                                    agree.mutate({
                                                        moneypoolId: fundId!,
                                                        demandId: demandId!,
                                                    })
                                                }
                                                className=" flex-1 rounded-full bg-green-1 py-2.5 text-sm font-bold text-white disabled:opacity-50"
                                            >
                                                {agree.isLoading
                                                    ? 'در حال ثبت…'
                                                    : 'بله، موافقم'}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => setVoting(null)}
                                                className=" flex-1 rounded-full border border-stroke bg-white py-2.5 text-sm text-text-body"
                                            >
                                                انصراف
                                            </button>
                                        </div>
                                    </div>
                                )}

                                {/* «مخالفم» takes its reason — «دلیل هم ازش
                                    بگیر». Not a comment: a comment is public
                                    and texts the member. */}
                                {voting === 'disagree' && (
                                    <div className=" mt-4 rounded-xl border border-stroke bg-canvas p-3">
                                        <label
                                            htmlFor="loan-demand-objection"
                                            className=" block text-xs font-bold text-heading"
                                        >
                                            دلیل مخالفت
                                        </label>
                                        <p className=" mt-1 text-[11px] leading-6 text-text-secondary">
                                            فقط مدیران صندوق این دلیل را
                                            می‌بینند و برای عضو پیامکی نمی‌رود.
                                        </p>
                                        <textarea
                                            id="loan-demand-objection"
                                            value={reason}
                                            onChange={(event) =>
                                                setReason(event.target.value)
                                            }
                                            maxLength={1000}
                                            rows={3}
                                            placeholder="مثلاً: هنوز قسط وام قبلی‌اش عقب است."
                                            className=" mt-2 w-full resize-none rounded-lg border border-stroke bg-white p-3 text-xs leading-6 outline-none focus:border-brand"
                                        />
                                        <div className=" mt-2 flex gap-2">
                                            <button
                                                type="button"
                                                disabled={
                                                    objection.isLoading ||
                                                    !reason.trim()
                                                }
                                                onClick={() =>
                                                    objection.mutate({
                                                        moneypoolId: fundId!,
                                                        demandId: demandId!,
                                                        note: reason.trim(),
                                                    })
                                                }
                                                className=" flex-1 rounded-full bg-danger-on py-2.5 text-sm font-bold text-white disabled:opacity-50"
                                            >
                                                {objection.isLoading
                                                    ? 'در حال ثبت…'
                                                    : 'ثبت مخالفت'}
                                            </button>
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setVoting(null)
                                                    setReason('')
                                                }}
                                                className=" flex-1 rounded-full border border-stroke bg-white py-2.5 text-sm text-text-body"
                                            >
                                                انصراف
                                            </button>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}

                        {/* ── the timeline ─────────────────────────────── */}
                        <p className=" mb-2 mt-5 px-1 text-sm font-bold text-heading">
                            چه اتفاقی افتاده
                        </p>
                        {/* The backend derives «درخواست وام ثبت شد» from the
                            request itself, so this list is never legitimately
                            empty. It was, for every request created before the
                            کارتابل shipped — an empty rounded box under the
                            heading, reported on demand 9347. Keeping a real
                            empty state means a future gap says so instead of
                            rendering a blank card. */}
                        {(data.events ?? []).length === 0 ? (
                            <p className=" rounded-2xl border border-stroke bg-white px-4 py-4 text-center text-[11px] leading-6 text-text-muted">
                                هنوز رویدادی برای این درخواست ثبت نشده است.
                            </p>
                        ) : (
                            <ol className=" rounded-2xl border border-stroke bg-white px-4 py-2">
                                {/* oldest first — the app-wide rule, see
                                inTimelineOrder */}
                                {inTimelineOrder(data.events).map((event) => {
                                    const isComment = event.kind === 'cmt'
                                    const isNote = event.kind === 'mnt'
                                    const isVote = VOTE_KINDS.includes(
                                        event.kind
                                    )
                                    const isPrivate =
                                        MANAGER_ONLY_KINDS.includes(event.kind)
                                    return (
                                        <li
                                            key={event.id}
                                            className=" flex gap-3 border-b border-divider py-3 last:border-b-0"
                                        >
                                            <span
                                                aria-hidden
                                                className={` mt-1 h-2 w-2 shrink-0 rounded-full ${
                                                    event.kind === 'acc' ||
                                                    event.kind === 'pay' ||
                                                    event.kind === 'lik'
                                                        ? ' bg-success-1'
                                                        : event.kind ===
                                                              'rej' ||
                                                          event.kind === 'dis'
                                                        ? ' bg-danger-on'
                                                        : isComment
                                                        ? ' bg-brand'
                                                        : isNote
                                                        ? ' bg-text-secondary'
                                                        : ' bg-stroke'
                                                }`}
                                            />
                                            <span className=" min-w-0 flex-1">
                                                <span className=" flex flex-wrap items-center gap-x-2 gap-y-1 text-xs font-bold text-text-body">
                                                    {isComment
                                                        ? event.actor_is_requester
                                                            ? isRequester
                                                                ? 'پیام شما'
                                                                : `پیام ${event.actor_name}`
                                                            : `پیام ${
                                                                  event.actor_name ||
                                                                  'مدیر'
                                                              }${
                                                                  // beside the notes, say
                                                                  // which ones the member read
                                                                  canNote
                                                                      ? ' به عضو'
                                                                      : ''
                                                              }`
                                                        : isNote
                                                        ? `یادداشت ${
                                                              event.actor_name ||
                                                              'مدیر'
                                                          }`
                                                        : isVote
                                                        ? `${
                                                              event.actor_name ||
                                                              'یک مدیر'
                                                          } ${
                                                              eventLabels[
                                                                  event.kind
                                                              ]
                                                          }`
                                                        : eventLabels[
                                                              event.kind
                                                          ] ?? 'رویداد'}
                                                    {isPrivate && (
                                                        <ManagersOnly />
                                                    )}
                                                </span>
                                                <span className=" mt-[2px] block text-[11px] text-text-muted">
                                                    {longJalali(event.created)}
                                                    {!isComment &&
                                                    !isPrivate &&
                                                    event.actor_name
                                                        ? `، ${event.actor_name}`
                                                        : ''}
                                                </span>
                                                {event.note && (
                                                    <span
                                                        className={` mt-1 block whitespace-pre-line text-[11px] leading-6 ${
                                                            isNote
                                                                ? ' rounded-lg border border-dashed border-stroke bg-white px-3 py-2 text-text-body'
                                                                : isComment ||
                                                                  isVote
                                                                ? ' rounded-lg bg-canvas px-3 py-2 text-text-body'
                                                                : ' text-text-secondary'
                                                        }`}
                                                    >
                                                        {event.note}
                                                    </span>
                                                )}
                                                {/* «درخواست پذیرفته شد» → where
                                                    the money is. Only when
                                                    Hamyan paid out of موجودی
                                                    نزد همیان; the server sends
                                                    nothing otherwise. */}
                                                {event.transfer && (
                                                    <TransferTrackingLink
                                                        transfer={
                                                            event.transfer
                                                        }
                                                        fundId={fundId}
                                                        demandId={demandId}
                                                        viewer={
                                                            isManager
                                                                ? 'manager'
                                                                : 'requester'
                                                        }
                                                    />
                                                )}
                                            </span>
                                        </li>
                                    )
                                })}
                            </ol>
                        )}

                        {/* ── the composer ─────────────────────────────── */}
                        {data.viewer.can_comment && (
                            <div className=" mt-4 rounded-2xl border border-stroke bg-white p-4">
                                <label
                                    htmlFor="loan-demand-comment"
                                    className=" block text-sm font-bold text-heading"
                                >
                                    پیام بگذارید
                                </label>
                                {/* Two audiences for a manager — owner's
                                    ask: a note the member never reads, or a
                                    message that reaches them by SMS. */}
                                {canNote && (
                                    <div
                                        role="radiogroup"
                                        aria-label="این پیام برای کیست؟"
                                        className=" mt-3 grid grid-cols-2 gap-1 rounded-full bg-canvas p-1"
                                    >
                                        {(
                                            [
                                                ['managers', 'فقط مدیران'],
                                                ['member', 'برای عضو'],
                                            ] as [TLoanDemandAudience, string][]
                                        ).map(([value, label]) => (
                                            <button
                                                key={value}
                                                type="button"
                                                role="radio"
                                                aria-checked={
                                                    audience === value
                                                }
                                                onClick={() =>
                                                    chooseAudience(value)
                                                }
                                                className={` flex min-h-[40px] items-center justify-center gap-1.5 rounded-full text-xs font-bold ${
                                                    audience === value
                                                        ? ' bg-white text-heading shadow-sm'
                                                        : ' text-text-secondary'
                                                }`}
                                            >
                                                {value === 'managers' && (
                                                    <FiLock
                                                        size={12}
                                                        aria-hidden
                                                    />
                                                )}
                                                {label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                                <p className=" mt-2 text-[11px] leading-6 text-text-secondary">
                                    {/* «مخالفم» only exists while the request
                                        is open — pointing a manager at a
                                        button that is no longer on the page
                                        is worse than saying nothing. The
                                        thread itself stays open after the
                                        decision, which is the whole point. */}
                                    {isRequester
                                        ? 'مدیران صندوق پیام شما را می‌بینند و برایشان پیامک می‌رود.'
                                        : noting
                                        ? 'فقط مدیران صندوق این یادداشت را می‌بینند. عضو آن را نمی‌بیند و برای هیچ‌کس پیامک نمی‌رود.'
                                        : canNote
                                        ? 'عضو این پیام را می‌بیند و برایش پیامک می‌رود.'
                                        : data.is_open
                                        ? 'عضو پیام شما را می‌بیند و برایش پیامک می‌رود. برای مخالفت از دکمهٔ «مخالفم» استفاده کنید تا عضو آن را نبیند.'
                                        : 'عضو پیام شما را می‌بیند و برایش پیامک می‌رود. این گفت‌وگو بعد از تصمیم هم باز می‌ماند.'}
                                </p>
                                <textarea
                                    id="loan-demand-comment"
                                    value={draft}
                                    onChange={(event) =>
                                        setDraft(event.target.value)
                                    }
                                    maxLength={1000}
                                    rows={3}
                                    placeholder={
                                        noting
                                            ? 'مثلاً: با ضامنش تماس گرفتم، تأیید کرد.'
                                            : isManager
                                            ? 'مثلاً: ضامن دوم شما چه کسی است؟'
                                            : 'مثلاً: آقای احمدی ضامن می‌شود.'
                                    }
                                    className={` mt-3 w-full resize-none rounded-lg border p-3 text-xs leading-6 outline-none focus:border-brand ${
                                        noting
                                            ? ' border-dashed border-stroke bg-white'
                                            : ' border-stroke bg-canvas'
                                    }`}
                                />
                                <button
                                    type="button"
                                    disabled={
                                        comment.isLoading || !draft.trim()
                                    }
                                    onClick={() =>
                                        comment.mutate({
                                            moneypoolId: fundId!,
                                            demandId: demandId!,
                                            note: draft.trim(),
                                            // only to a server that said it
                                            // understands it — see `canNote`
                                            ...(canNote ? { audience } : {}),
                                        })
                                    }
                                    className=" mt-3 w-full rounded-full bg-green-1 py-3 text-sm font-bold text-white disabled:opacity-50"
                                >
                                    {comment.isLoading
                                        ? 'در حال ارسال…'
                                        : noting
                                        ? 'ثبت یادداشت برای مدیران'
                                        : canNote
                                        ? 'ارسال پیام به عضو'
                                        : 'ارسال پیام'}
                                </button>
                            </div>
                        )}

                        {/* ── the loan it became ───────────────────────── */}
                        {/* The other half of «بعد از تأیید هم بتوانم ببینمش»:
                            from here the thread is one tap from the loan, and
                            from the loan (DemandLink) one tap back. Shown to
                            the borrower too — it is their loan — and to
                            nobody else, because nobody else gets this page. */}
                        {Boolean(data.loan_id) && (
                            <button
                                type="button"
                                onClick={openLoan}
                                className=" mt-5 flex w-full items-center justify-between gap-3 rounded-2xl border border-stroke bg-white p-4 text-right active:bg-canvas"
                            >
                                <span className=" flex items-center gap-3">
                                    <span
                                        aria-hidden
                                        className=" flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-success-2 text-success-on"
                                    >
                                        <FiCheckCircle className=" h-5 w-5" />
                                    </span>
                                    <span className=" min-w-0">
                                        <span className=" block text-sm font-bold text-heading">
                                            وامی که از این درخواست داده شد
                                        </span>
                                        <span className=" mt-[2px] block text-[11px] text-text-secondary">
                                            {data.loan_amount
                                                ? `${toman(
                                                      data.loan_amount
                                                  )} — مشاهدهٔ اقساط`
                                                : 'مشاهدهٔ اقساط'}
                                        </span>
                                    </span>
                                </span>
                                <FiChevronLeft
                                    aria-hidden
                                    className=" h-5 w-5 shrink-0 text-text-muted"
                                />
                            </button>
                        )}

                        {/* ── what a manager can do about it ───────────── */}
                        {isManager && data.is_open && (
                            <button
                                type="button"
                                onClick={grantThis}
                                className=" mt-5 w-full rounded-full bg-green-1 py-3 text-sm font-bold text-white"
                            >
                                بررسی و اعطای وام
                            </button>
                        )}

                        {isManager && (
                            <button
                                type="button"
                                onClick={() =>
                                    navigate(`/loans/requested-loans/${fundId}`)
                                }
                                className=" mt-3 w-full rounded-full border border-green-1 bg-white py-3 text-sm font-bold text-green-1"
                            >
                                همهٔ درخواست‌های وام
                            </button>
                        )}
                    </>
                )}
            </div>
        </div>
    )
}
